import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useParams } from 'react-router-dom';
import { getUserTransactions } from 'service';
import { colorTheme } from 'theme';
import {
  commonConstantTexts,
  fundTypeOption,
  orderTypeOption,
  transactionTypes,
} from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { CustomDataGrid } from 'components';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import {
  checkSortingValues,
  dateFormat,
  UnitFormater,
} from 'utils/GeneralUtil';
import { useSelector, useDispatch } from 'react-redux';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { getZpUserTransactions } from 'service/UserService';
import { LoadingButton } from '@mui/lab';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { getFundType } from 'utils/Constants';

export interface TransactionType {
  id: number;
  allocationAmount: string;
  date: string;
  fund: { symbol: string };
  unitsAllocated: string;
}
export interface TransactionRow {
  id: number;
  type: string;
  transactionType: string;
  amount?: number | string;
  units?: number | string;
  [key: string]: unknown;
}
const useStyles = makeStyles({
  paperContainer: {
    width: '100%',
    padding: '10px',
    marginBottom: '20px',
  },
  emptyTransactionContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '70vh',
    width: '100%',
  },
  emptyTransactionText: {
    fontFamily: 'Inter!important',
    color: '#838383',
    fontWeight: 500,
    fontSize: '1.125rem',
    lineHeight: 1.2,
  },
});

const Transactions = (): JSX.Element => {
  const { id } = useParams();
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);

  const [transactions, setTransactions] = useState<TransactionType[]>([]);
  const [pageCount, setPageCount] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const pageInfo = useSelector((state) => state?.pagination?.allTransaction);
  const [filters, setFilters] = useState<unknown>({
    startDate: null,
    endDate: null,
    oStartDate: null,
    oEndDate: null,
    sStartDate: null,
    sEndDate: null,
    status: '',
    fundType: '',
    orderType: '',
  });
  const getTransactions = (): void => {
    if (id) {
      getUserTransactions(
        id,
        'contribution',
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        searchValue,
        filters,
      )
        .then(({ result }) => {
          setTransactions(result.rows);
          setPageCount(result?.count);
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      getZpUserTransactions(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        searchValue,
        filters,
      )
        .then(({ result }) => {
          setTransactions(result.rows);
          setPageCount(result?.count);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  useEffect(() => {
    getTransactions();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const handleApplyFilter = async (): Promise<void> => {
    dispatch(
      setCurrentPage({ key: commonConstantTexts.allTransaction, value: 0 }),
    );
    getTransactions();
  };

  const getTransactionType = (type: string): string => {
    switch (type) {
      case transactionTypes.buyOrder:
        return strings.displayText.buy;
      case transactionTypes.sellOrder:
        return strings.displayText.sell;
      case transactionTypes.monthlyCharge:
        return strings.displayText.charges;
      case transactionTypes.contributions:
        return strings.displayText.contributionReceived;
      case transactionTypes.reAllocation:
        return strings.displayText.contributionAdjustment;
      default:
        return type;
    }
  };

  const getTransactionSign = (row: TransactionRow, isuser: boolean): string => {
    if (row?.type === transactionTypes.monthlyCharge) {
      return isuser ? '-' : '+';
    }
    if (row?.transactionType === transactionTypes.buy) {
      return '-';
    }
    if (row?.transactionType === transactionTypes.sell) {
      return '-';
    }
    return '+';
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'id',
      sortable: !id,
      width: 75,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          ID
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.id}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      sortable: !id,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.allocatedDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.date)}
        </Typography>
      ),
    },

    {
      field: 'fundType',
      sortable: false,
      width: 150,
      cellClassName: 'name-column--cell',
      hide: true,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundType}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {getFundType(row?.fundType as string)}
        </Typography>
      ),
    },
    {
      field: 'orderType',
      sortable: false,
      width: 220,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.orderType}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {getTransactionType(row?.type)}
        </Typography>
      ),
    },
    {
      field: 'fund.description',
      sortable: !id,
      width: 450,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row?.fundName} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row?.shortDescription || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'amount',
      sortable: false,
      width: 150,
      cellClassName: 'name-column--cell',
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.amountEuro}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {`${getTransactionSign(row, Boolean(id))} ${CurrencyFormater(
            Number(Math.abs(row?.amount)),
          )}`}
        </Typography>
      ),
    },
    {
      field: 'units',
      sortable: false,
      width: 150,
      cellClassName: 'name-column--cell',
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.units}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.units
            ? `${getTransactionSign(row, Boolean(id))} ${UnitFormater(
                Number(Math.abs(row?.units)),
              )}`
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'orderInfo.orderDate',
      sortable: !id,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.orderDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.type !== transactionTypes.monthlyCharge && row?.orderDate
            ? dateFormat(row?.orderDate)
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'orderInfo.settlementDate',
      sortable: !id,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.settlementDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.type !== transactionTypes.monthlyCharge &&
          row.status === 'Completed' &&
          row?.settlementDate
            ? dateFormat(row?.settlementDate)
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      sortable: false,
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.status || '-'}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      {!id ? (
        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ margin: '5px' }}
        >
          <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
            <TextField
              label={strings.displayText.allocationStartDate}
              type="date"
              value={filters.startDate}
              onChange={(e) => {
                handleFilterChange('startDate', e.target.value);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
            <TextField
              label={strings.displayText.allocationEndDate}
              type="date"
              value={filters.endDate}
              onChange={(e) => {
                handleFilterChange('endDate', e.target.value);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
            <TextField
              label={strings.displayText.orderStartDate}
              type="date"
              value={filters.oStartDate}
              onChange={(e) => {
                handleFilterChange('oStartDate', e.target.value);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
            <TextField
              label={strings.displayText.orderEndDate}
              type="date"
              value={filters.oEndDate}
              onChange={(e) => {
                handleFilterChange('oEndDate', e.target.value);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
            <TextField
              label={strings.displayText.settleStartDate}
              type="date"
              value={filters.sStartDate}
              onChange={(e) => {
                handleFilterChange('sStartDate', e.target.value);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
            <TextField
              label={strings.displayText.settleEndDate}
              type="date"
              value={filters.sEndDate}
              onChange={(e) => {
                handleFilterChange('sEndDate', e.target.value);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={4} />
          <Grid sx={{ minWidth: 300, marginTop: 2, marginRight: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Fund Type</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="Fund Type"
                value={filters.fundType}
                onChange={(e) => {
                  handleFilterChange('fundType', e.target.value);
                }}
              >
                {fundTypeOption.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid sx={{ minWidth: 300, marginTop: 2, marginRight: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Order Type</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="Order Type"
                value={filters.orderType}
                onChange={(e) => {
                  handleFilterChange('orderType', e.target.value);
                }}
              >
                {orderTypeOption.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              loadingPosition="end"
              onClick={() => {
                void handleApplyFilter();
              }}
            >
              {strings.displayText.filter}
            </LoadingButton>
          </Grid>
        </Grid>
      ) : (
        ''
      )}{' '}
      <Box>
        {transactions.length === 0 ? (
          <Box className={classes.emptyTransactionContainer}>
            <Typography className={classes.emptyTransactionText}>
              {strings.displayText.transcationEmptyText}
            </Typography>
          </Box>
        ) : (
          <CustomDataGrid
            columns={columns}
            rows={transactions}
            getRowId={(row: Request) => `${String(row?.id)}`}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? commonConstantTexts.evenText
                : commonConstantTexts.oddText
            }
            currentPage={commonConstantTexts.allTransaction}
            pageCount={pageCount}
            searchEnabled={true}
            handleSearch={handleSearch}
            searchPlaceholder={strings.displayText.searchLabel5}
            sortingMode={strings.displayText.sortingServer}
            onSortModelChange={(model: unknown) => {
              dispatch(
                setSortBy({
                  key: commonConstantTexts.allTransaction,
                  value: model[0].field,
                }),
              );
              dispatch(
                setSortOrder({
                  key: commonConstantTexts.allTransaction,
                  value: model[0].sort,
                }),
              );
              dispatch(
                setCurrentPage({
                  key: commonConstantTexts.allTransaction,
                  value: 0,
                }),
              );
            }}
            sortModel={checkSortingValues(pageInfo)}
          />
        )}
      </Box>
    </Box>
  );
};

export default Transactions;
